const ERROR_MESSAGES = {
    "0": "No error",
    "101": "General exception",
    "103": "Already initialized",
    "104": "Already terminated",
    "122": "GetValue before initialization",
    "132": "SetValue before initialization",
    "142": "Commit before initialization",
    "201": "Invalid argument",
    "401": "Undefined data model element",
    "404": "Data model element is read only",
    "405": "Data model element is write only",
    "406": "Data model element type mismatch",
};

const READ_ONLY_12 = new Set([
    "cmi.core.student_id",
    "cmi.core.student_name",
    "cmi.core.credit",
    "cmi.core.entry",
    "cmi.core.total_time",
]);

const READ_ONLY_2004 = new Set([
    "cmi.learner_id",
    "cmi.learner_name",
    "cmi.credit",
    "cmi.entry",
    "cmi.total_time",
    "cmi._version",
]);

const PRIORITY_12 = new Set([
    "cmi.core.lesson_status",
    "cmi.core.score.raw",
    "cmi.core.score.min",
    "cmi.core.score.max",
    "cmi.core.lesson_location",
    "cmi.core.session_time",
    "cmi.core.total_time",
    "cmi.suspend_data",
]);

const PRIORITY_2004 = new Set([
    "cmi.completion_status",
    "cmi.success_status",
    "cmi.score.raw",
    "cmi.score.min",
    "cmi.score.max",
    "cmi.score.scaled",
    "cmi.location",
    "cmi.session_time",
    "cmi.total_time",
    "cmi.suspend_data",
]);

const NAVIGATION_2004 = new Set([
    "adl.nav.request",
]);

const RUSTICI_GOLF_PAGE_COUNTS = {
    playing: 5,
    etiquette: 3,
    handicapping: 4,
    havingfun: 2,
    assessment: 1,
};

const STATUS_12 = new Set(["passed", "completed", "failed", "incomplete", "browsed", "not attempted"]);
const COMPLETION_2004 = new Set(["completed", "incomplete", "not attempted", "unknown"]);
const SUCCESS_2004 = new Set(["passed", "failed", "unknown"]);
const EXTENSIBLE = /^cmi\.(interactions|objectives)\.\d+\..+/;
const SAFE_CMI_KEY = /^cmi(\.[A-Za-z0-9_]+|\.\d+)+$/;
const MAX_SUSPEND_DATA = 64000;
const WATCHED_CMI_KEY = /^(cmi\.interactions\.|cmi\.objectives\.|cmi\.core\.score\.raw$|cmi\.score\.raw$|cmi\.core\.lesson_status$|cmi\.completion_status$|cmi\.success_status$)/;

const stringify = (value) => (value === undefined || value === null ? "" : String(value));
const emptyArgument = (argument) => argument === "" || argument === undefined || argument === null;
const scormDebugEnabled = () => process.env.NODE_ENV !== "production";

export const attachScormApiToWindow = (targetWindow, sourceWindow = window, label = "window") => {
    if (!targetWindow || !sourceWindow) return false;

    try {
        if (sourceWindow.API) {
            targetWindow.API = sourceWindow.API;
        }
        if (sourceWindow.API_1484_11) {
            targetWindow.API_1484_11 = sourceWindow.API_1484_11;
        }
        if (sourceWindow.RecordQuestion) {
            targetWindow.RecordQuestion = sourceWindow.RecordQuestion;
        }
        if (sourceWindow.RecordTest) {
            targetWindow.RecordTest = sourceWindow.RecordTest;
        }
        if (scormDebugEnabled()) {
            console.info("[SCORM API]", "AttachAPI", {
                target: label,
                hasApi12: Boolean(targetWindow.API),
                hasApi2004: Boolean(targetWindow.API_1484_11),
                hasRecordQuestion: Boolean(targetWindow.RecordQuestion),
                hasRecordTest: Boolean(targetWindow.RecordTest),
            });
        }
        return Boolean(targetWindow.API || targetWindow.API_1484_11 || targetWindow.RecordQuestion || targetWindow.RecordTest);
    } catch (error) {
        if (scormDebugEnabled()) {
            console.warn("[SCORM API]", "AttachAPI failed", { target: label, error });
        }
        return false;
    }
};

export function installScormApiBridge(config) {
    const previousApi12 = window.API;
    const previousApi2004 = window.API_1484_11;
    const previousRecordQuestion = window.RecordQuestion;
    const previousRecordTest = window.RecordTest;
    const runtimeValues = { ...(config.runtimeValues || {}) };
    const pendingValues = {};
    let initialized = false;
    let terminated = config.initializedStatus === "TERMINATED";
    let lastError = "0";
    let debounceTimer = null;
    let disposed = false;
    let initializePromise = null;
    const debugEnabled = scormDebugEnabled();

    const debug = (method, payload = {}) => {
        if (!debugEnabled) return;
        const key = payload.key || payload.element;
        if (typeof config.onApiCall === "function") {
            config.onApiCall({ method, payload, at: new Date().toISOString() });
        }
        if (!key || WATCHED_CMI_KEY.test(key) || ["Initialize", "LMSInitialize", "Commit", "LMSCommit", "Terminate", "LMSFinish", "RecordQuestion", "RecordTest"].includes(method)) {
            console.info("[SCORM API]", method, payload);
        }
    };

    const basePayload = () => ({
        registrationId: config.registrationId,
        attemptId: config.attemptId,
        packageId: config.packageId,
        scoId: config.scoId,
        nodeId: config.nodeId,
    });

    const setError = (code) => {
        lastError = String(code || "0");
        return lastError === "0";
    };

    const is2004 = () => config.version === "SCORM_2004" || config.runtimeVersion === "SCORM_2004";
    const knownKey = (key) => {
        if (!key || typeof key !== "string") return false;
        if (NAVIGATION_2004.has(key)) return true;
        if (!key.startsWith("cmi.")) return false;
        const priority = is2004() ? PRIORITY_2004 : PRIORITY_12;
        const readOnly = is2004() ? READ_ONLY_2004 : READ_ONLY_12;
        return priority.has(key) || readOnly.has(key) || EXTENSIBLE.test(key) || SAFE_CMI_KEY.test(key);
    };
    const readOnlyKey = (key) => (is2004() ? READ_ONLY_2004 : READ_ONLY_12).has(key);
    const validNumber = (value) => value !== "" && !Number.isNaN(Number(value));
    const validValue = (key, value) => {
        const normalized = stringify(value).trim();
        if (key.endsWith(".score.raw") || key.endsWith(".score.min") || key.endsWith(".score.max") || key === "cmi.score.scaled") {
            if (!validNumber(normalized)) return false;
        }
        if (key === "cmi.score.scaled") {
            const scaled = Number(normalized);
            if (scaled < -1 || scaled > 1) return false;
        }
        if (key === "cmi.core.lesson_status" && !STATUS_12.has(normalized)) return false;
        if (key === "cmi.completion_status" && !COMPLETION_2004.has(normalized)) return false;
        if (key === "cmi.success_status" && !SUCCESS_2004.has(normalized)) return false;
        if (key === "cmi.suspend_data" && normalized.length > MAX_SUSPEND_DATA) return false;
        return true;
    };

    const sequencingRefreshNeeded = (state) => {
        if (!state) return false;
        const lessonStatus = stringify(state.lessonStatus).toLowerCase();
        const completionStatus = stringify(state.completionStatus).toLowerCase();
        const successStatus = stringify(state.successStatus).toLowerCase();
        return completionStatus === "completed"
            || successStatus === "passed"
            || lessonStatus === "completed"
            || lessonStatus === "passed"
            || Number(state.completionPercent || 0) >= 100;
    };

    const applyBackendState = (response, options = {}) => {
        const state = response?.data?.state || response?.state;
        if (state?.runtimeValues) {
            Object.assign(runtimeValues, state.runtimeValues);
        }
        if (state?.attemptStatus === "TERMINATED") {
            terminated = true;
        }
        if (state && typeof config.onRuntimeStateChange === "function") {
            config.onRuntimeStateChange(state);
        }
        if (state && options.refreshSequencing && sequencingRefreshNeeded(state)) {
            config.onSequencingRefresh?.(state);
        }
    };

    const ensureInitializedOnBackend = async () => {
        if (initializePromise) {
            await initializePromise;
        }
    };

    const flushPending = async () => {
        if (disposed || terminated || Object.keys(pendingValues).length === 0) return true;
        await ensureInitializedOnBackend();
        const values = { ...pendingValues };
        Object.keys(pendingValues).forEach((key) => delete pendingValues[key]);
        try {
            config.onSaveState?.("saving");
            const response = await config.callbacks.setValues({ ...basePayload(), values });
            applyBackendState(response, { refreshSequencing: true });
            const ok = response?.data?.success ?? response?.success ?? true;
            if (!ok) setError(response?.data?.errorCode || response?.errorCode || "101");
            config.onSaveState?.(ok ? "saved" : "error");
            return Boolean(ok);
        } catch (error) {
            Object.assign(pendingValues, values);
            setError("101");
            config.onSaveState?.("error");
            return false;
        }
    };

    const scheduleCommit = () => {
        if (debounceTimer) window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(async () => {
            await flushPending();
            await commitNow();
        }, 1200);
    };

    const initialize = () => {
        debug(is2004() ? "Initialize" : "LMSInitialize", { attemptId: config.attemptId, scoId: config.scoId });
        if (terminated) return setError("104") ? "true" : "false";
        if (initialized) return setError("103") ? "true" : "false";
        initialized = true;
        setError("0");
        initializePromise = config.callbacks
            .initialize({ ...basePayload(), version: config.runtimeVersion || config.version })
            .then(applyBackendState)
            .catch((error) => {
                console.error("[SCORM API] Initialize backend failed", error);
                setError("101");
            })
            .finally(() => {
                initializePromise = null;
            });
        return "true";
    };

    const normalizeReturnedValue = (key, value) => {
        const normalized = stringify(value);
        if (key !== "cmi.location") {
            return normalized;
        }

        const launchUrl = stringify(config.launchUrl).toLowerCase();
        const contentMatch = launchUrl.match(/[?&]content=([^&#]+)/);
        const contentKey = contentMatch ? decodeURIComponent(contentMatch[1]) : "";
        const pageCount = RUSTICI_GOLF_PAGE_COUNTS[contentKey];
        if (!pageCount) {
            return normalized;
        }

        if (!/^\d+$/.test(normalized)) {
            return "";
        }
        const pageIndex = Number(normalized);
        return pageIndex >= 0 && pageIndex < pageCount ? normalized : "";
    };

    const getValue = (key) => {
        debug(is2004() ? "GetValue" : "LMSGetValue", { key });
        if (!initialized) {
            setError("122");
            return "";
        }
        if (terminated) {
            setError("104");
            return "";
        }
        if (!knownKey(key)) {
            setError("401");
            return "";
        }
        setError("0");
        return normalizeReturnedValue(key, runtimeValues[key]);
    };

    const setValue = (key, value) => {
        debug(is2004() ? "SetValue" : "LMSSetValue", { key, value });
        if (!initialized) return setError("132") ? "true" : "false";
        if (terminated) return setError("104") ? "true" : "false";
        if (!knownKey(key)) return setError("401") ? "true" : "false";
        if (readOnlyKey(key)) return setError("404") ? "true" : "false";
        if (!validValue(key, value)) return setError("406") ? "true" : "false";
        const stringValue = stringify(value);
        runtimeValues[key] = stringValue;
        pendingValues[key] = stringValue;
        setError("0");
        scheduleCommit();
        return "true";
    };

    const commitNow = async () => {
        debug(is2004() ? "Commit" : "LMSCommit", { attemptId: config.attemptId, pendingCount: Object.keys(pendingValues).length });
        if (!initialized) {
            setError("142");
            return false;
        }
        if (terminated) {
            setError("104");
            return false;
        }
        await flushPending();
        try {
            config.onSaveState?.("saving");
            const response = await config.callbacks.commit({ ...basePayload(), values: {} });
            applyBackendState(response, { refreshSequencing: true });
            const ok = response?.data?.success ?? response?.success ?? true;
            setError(ok ? "0" : response?.data?.errorCode || response?.errorCode || "101");
            config.onSaveState?.(ok ? "saved" : "error");
            return Boolean(ok);
        } catch (error) {
            setError("101");
            config.onSaveState?.("error");
            return false;
        }
    };

    const commit = () => {
        commitNow();
        return lastError === "0" ? "true" : "false";
    };

    const ensureRuntimeStarted = () => {
        if (!initialized && !terminated) {
            initialize();
        }
    };

    const interactionIndexById = new Map();
    const stableInteractionIndex = (interactionId) => {
        const text = stringify(interactionId);
        if (!text) return interactionIndexById.size;
        let hash = 0;
        for (let index = 0; index < text.length; index += 1) {
            hash = (hash * 31 + text.charCodeAt(index)) % 900000;
        }
        return 10000 + hash;
    };

    const getInteractionIndex = (interactionId) => {
        const key = stringify(interactionId) || `interaction_${interactionIndexById.size}`;
        if (!interactionIndexById.has(key)) {
            interactionIndexById.set(key, stableInteractionIndex(key));
        }
        return interactionIndexById.get(key);
    };

    const recordQuestion = (interactionId, description, type, learnerResponse, correctResponse, wasCorrect, objectiveId) => {
        debug("RecordQuestion", { interactionId, type, learnerResponse, correctResponse, wasCorrect, objectiveId });
        ensureRuntimeStarted();
        const index = getInteractionIndex(interactionId);
        const prefix = `cmi.interactions.${index}`;
        const result = wasCorrect ? "correct" : "incorrect";

        setValue(`${prefix}.id`, interactionId);
        setValue(`${prefix}.type`, type);
        setValue(is2004() ? `${prefix}.learner_response` : `${prefix}.student_response`, learnerResponse);
        setValue(`${prefix}.correct_responses.0.pattern`, correctResponse);
        setValue(`${prefix}.result`, result);
        if (description) {
            setValue(`${prefix}.description`, description);
        }
        if (objectiveId) {
            setValue(`${prefix}.objectives.0.id`, objectiveId);
        }
        return "true";
    };

    const recordTest = (score) => {
        debug("RecordTest", { score });
        ensureRuntimeStarted();
        const numericScore = Number(score);
        const normalizedScore = Number.isNaN(numericScore) ? 0 : numericScore;
        const success = normalizedScore >= 80 ? "passed" : "failed";

        if (is2004()) {
            setValue("cmi.score.raw", normalizedScore);
            setValue("cmi.score.min", "0");
            setValue("cmi.score.max", "100");
            setValue("cmi.score.scaled", String(Math.max(-1, Math.min(1, normalizedScore / 100))));
            setValue("cmi.completion_status", "completed");
            setValue("cmi.success_status", success);
        } else {
            setValue("cmi.core.score.raw", normalizedScore);
            setValue("cmi.core.score.min", "0");
            setValue("cmi.core.score.max", "100");
            setValue("cmi.core.lesson_status", success);
        }
        commitNow();
        return "true";
    };

    const finish = () => {
        debug(is2004() ? "Terminate" : "LMSFinish", { attemptId: config.attemptId, pendingCount: Object.keys(pendingValues).length });
        if (!initialized) return setError("201") ? "true" : "false";
        if (terminated) return setError("104") ? "true" : "false";
        terminated = true;
        if (debounceTimer) window.clearTimeout(debounceTimer);
        const values = { ...pendingValues };
        Object.keys(pendingValues).forEach((key) => delete pendingValues[key]);
        ensureInitializedOnBackend().then(() => config.callbacks
            .finish({ ...basePayload(), values, terminated: true })
            .then((response) => {
                applyBackendState(response, { refreshSequencing: true });
                config.onSaveState?.("saved");
            })
            .catch(() => {
                setError("101");
                config.onSaveState?.("error");
            }));
        setError("0");
        return "true";
    };

    const finalFlush = () => {
        if (!initialized || terminated || Object.keys(pendingValues).length === 0) return;
        const values = { ...pendingValues };
        Object.keys(pendingValues).forEach((key) => delete pendingValues[key]);
        config.onSaveState?.("saving");
        config.callbacks.commit({ ...basePayload(), values }).catch(() => {
            Object.assign(pendingValues, values);
            config.onSaveState?.("error");
        });
    };

    const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") finalFlush();
    };
    const onPageHide = () => finalFlush();
    const onBeforeUnload = () => finalFlush();

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);

    const api12 = {
        LMSInitialize: (argument) => (emptyArgument(argument) ? initialize() : setError("201") ? "true" : "false"),
        LMSFinish: (argument) => (emptyArgument(argument) ? finish() : setError("201") ? "true" : "false"),
        LMSGetValue: getValue,
        LMSSetValue: setValue,
        LMSCommit: (argument) => (emptyArgument(argument) ? commit() : setError("201") ? "true" : "false"),
        LMSGetLastError: () => lastError,
        LMSGetErrorString: (code) => ERROR_MESSAGES[String(code)] || "Unknown error",
        LMSGetDiagnostic: (code) => ERROR_MESSAGES[String(code || lastError)] || "",
    };

    const api2004 = {
        Initialize: (argument) => (emptyArgument(argument) ? initialize() : setError("201") ? "true" : "false"),
        Terminate: (argument) => (emptyArgument(argument) ? finish() : setError("201") ? "true" : "false"),
        GetValue: getValue,
        SetValue: setValue,
        Commit: (argument) => (emptyArgument(argument) ? commit() : setError("201") ? "true" : "false"),
        GetLastError: () => lastError,
        GetErrorString: (code) => ERROR_MESSAGES[String(code)] || "Unknown error",
        GetDiagnostic: (code) => ERROR_MESSAGES[String(code || lastError)] || "",
    };

    api12.Initialize = api2004.Initialize;
    api12.Terminate = api2004.Terminate;
    api12.GetValue = api2004.GetValue;
    api12.SetValue = api2004.SetValue;
    api12.Commit = api2004.Commit;
    api12.GetLastError = api2004.GetLastError;
    api12.GetErrorString = api2004.GetErrorString;
    api12.GetDiagnostic = api2004.GetDiagnostic;

    api2004.LMSInitialize = api12.LMSInitialize;
    api2004.LMSFinish = api12.LMSFinish;
    api2004.LMSGetValue = api12.LMSGetValue;
    api2004.LMSSetValue = api12.LMSSetValue;
    api2004.LMSCommit = api12.LMSCommit;
    api2004.LMSGetLastError = api12.LMSGetLastError;
    api2004.LMSGetErrorString = api12.LMSGetErrorString;
    api2004.LMSGetDiagnostic = api12.LMSGetDiagnostic;

    window.API = api12;
    window.API_1484_11 = api2004;
    window.RecordQuestion = recordQuestion;
    window.RecordTest = recordTest;
    attachScormApiToWindow(window, window, "parent");
    if (window.top && window.top !== window) {
        attachScormApiToWindow(window.top, window, "top");
    }
    if (window.parent && window.parent !== window) {
        attachScormApiToWindow(window.parent, window, "parent.parent");
    }
    if (window.opener) {
        attachScormApiToWindow(window.opener, window, "opener");
    }

    debug("BridgeInstalled", {
        version: config.runtimeVersion || config.version,
        registrationId: config.registrationId,
        attemptId: config.attemptId,
        scoId: config.scoId,
        hasApi12: Boolean(window.API),
        hasApi2004: Boolean(window.API_1484_11),
    });

    return () => {
        disposed = true;
        if (debounceTimer) window.clearTimeout(debounceTimer);
        finalFlush();
        window.removeEventListener("beforeunload", onBeforeUnload);
        window.removeEventListener("pagehide", onPageHide);
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.API = previousApi12;
        window.API_1484_11 = previousApi2004;
        window.RecordQuestion = previousRecordQuestion;
        window.RecordTest = previousRecordTest;
    };
}
